import React, { useState, useEffect, useCallback } from 'react';
import { api } from './api/api';

// Group Spending v2: every balance change of a manager (GPRO MoneyHistory) with our breakdown,
// plus admin-configured tracked managers (extra collection schedule, predicted car).
// Backend: /api/v1/group-spending/money/{group}, /tracked, /managers/{group}, /money/collect.

const money = (v) => v == null ? '—' : (v < 0 ? '−$' : '+$') + Math.abs(v).toLocaleString('de-DE');
const fmtDt = (iso) => iso ? iso.replace('T', ' ').slice(0, 16) : '—';
const errText = (e, fallback) =>
  e.response?.status === 403 ? 'Access denied.' : (typeof e.response?.data === 'string' && e.response.data) || fallback;

const CONF = {
  EXACT: { color: '#16a34a', title: 'Exact match' },
  LIKELY: { color: '#d97706', title: 'Most likely — several explanations possible' },
  UNKNOWN: { color: '#9ca3af', title: 'Not identified' },
};

// "25h, 24h30m, 2h, 90m" <-> minutes before the end of qualifying
export const parseOffsets = (text) => text.split(/[,;\s]+/).map(s => s.trim().toLowerCase()).filter(Boolean).map(s => {
  const m = s.match(/^(?:(\d+)h)?(?:(\d+)m)?$/);
  if (!m || (!m[1] && !m[2])) return null;
  return (parseInt(m[1] || '0', 10) * 60) + parseInt(m[2] || '0', 10);
});
export const formatOffsets = (mins) => (mins || []).map(x => {
  const h = Math.floor(x / 60), m = x % 60;
  return (h ? `${h}h` : '') + (m ? `${m}m` : '') || '0m';
}).join(', ');

const CollectButton = ({ label, params, onDone }) => {
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true); setMsg(null);
    try {
      const res = await api.post(`/group-spending/money/collect?${params}`);
      setMsg(String(res.data));
      if (onDone) setTimeout(onDone, params.startsWith('idm') ? 5000 : 60000);
    } catch (e) {
      setMsg(errText(e, 'Failed.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <button type="button" className="fb-btn fb-btn-secondary fb-btn-sm" disabled={busy} onClick={run}>{busy ? '…' : label}</button>
      {msg && <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{msg}</span>}
    </span>
  );
};

// ── Transactions of a group ───────────────────────────────────────────────

export const GroupMoneyTransactions = ({ group, canCollect }) => {
  const [race, setRace] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(`/group-spending/money/${encodeURIComponent(group)}` + (race ? `?race=${race}` : ''));
      setData(res.data);
    } catch (e) {
      setError(errText(e, 'Failed to load transactions.'));
    } finally {
      setLoading(false);
    }
  }, [group, race]);

  useEffect(() => { setRace(null); }, [group]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="fb-card">
      <div className="fb-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span className="fb-card-title">
          {group} · balance changes {data?.race ? `· race ${data.race}` : ''}
        </span>
        <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {data?.races?.length > 0 && (
            <select value={data.race || ''} onChange={e => setRace(e.target.value)} className="fb-input fb-input-sm" style={{ width: 'auto' }}>
              {data.races.map(r => <option key={r} value={r}>Race {r}</option>)}
            </select>
          )}
          {canCollect && <CollectButton label="Collect group now" params={`group=${encodeURIComponent(group)}`} onDone={load} />}
        </span>
      </div>
      {loading && <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>}
      {!loading && error && <div style={{ padding: 16, color: 'var(--c-error)', fontSize: 13 }}>{error}</div>}
      {!loading && !error && data && data.managers.length === 0 && (
        <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>
          No transactions collected yet. They are collected 5 minutes after qualifying closes{canCollect ? ', or with “Collect group now”' : ''}.
        </div>
      )}
      {!loading && !error && data && data.managers.map(m => (
        <div key={m.idm} style={{ borderTop: '1px solid var(--c-border)', padding: '10px 16px' }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
            {m.name} {m.tracked && <span title="Tracked manager" style={{ fontSize: 11, color: 'var(--c-accent)' }}>👁️ tracked</span>}
            {canCollect && <span style={{ marginLeft: 10 }}><CollectButton label="↻" params={`idm=${m.idm}`} onDone={load} /></span>}
          </div>
          <table className="fb-table fb-table-sm">
            <tbody>
              {m.transactions.map((t, i) => (
                <tr key={i}>
                  <td style={{ whiteSpace: 'nowrap', color: 'var(--c-text-muted)', width: 130 }}>{fmtDt(t.dt)}</td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right', width: 120, color: t.amount < 0 ? 'var(--c-error)' : '#16a34a', fontWeight: 600 }}>{money(t.amount)}</td>
                  <td>
                    <span title={CONF[t.confidence]?.title} style={{ color: CONF[t.confidence]?.color, marginRight: 6 }}>●</span>
                    {t.label || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

// ── Tracked managers ──────────────────────────────────────────────────────

const CarView = ({ car }) => (
  <table className="fb-table fb-table-sm" style={{ maxWidth: 520 }}>
    <thead><tr><th>Part</th><th>Level</th><th>Detected from</th></tr></thead>
    <tbody>
      {car.map(p => (
        <tr key={p.part}>
          <td>{p.name}</td>
          <td style={{ fontWeight: 700 }}>{p.level == null ? '?' : p.level}</td>
          <td style={{ color: 'var(--c-text-muted)', fontSize: 12 }}>{p.level == null ? '—' : `${money(p.sourceAmount)} · ${fmtDt(p.sourceDt)}`}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const TRACKABLE_GROUPS = ['Elite', 'Master - 1', 'Master - 2', 'Master - 3', 'Master - 4', 'Master - 5'];

export const TrackedManagers = ({ group, canEdit }) => {
  const [list, setList] = useState([]);
  const [addGroup, setAddGroup] = useState(group);
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [newIdm, setNewIdm] = useState('');
  const [newOffsets, setNewOffsets] = useState('25h, 24h30m, 24h, 8h, 4h, 2h');
  const [edit, setEdit] = useState({});
  const [openCar, setOpenCar] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/group-spending/tracked');
      setList(res.data || []);
      setError(null);
    } catch (e) {
      setError(errText(e, 'Failed to load tracked managers.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setAddGroup(group); }, [group]);
  useEffect(() => {
    if (!canEdit) return;
    setCandidates([]);
    api.get(`/group-spending/managers/${encodeURIComponent(addGroup)}`).then(r => setCandidates(r.data || [])).catch(() => setCandidates([]));
    setNewIdm('');
  }, [addGroup, canEdit]);

  const save = async (idm, text) => {
    const offsets = parseOffsets(text);
    if (offsets.some(o => o == null)) { setError('Times must look like “25h, 24h30m, 90m”.'); return; }
    try {
      await api.put(`/group-spending/tracked/${idm}`, { offsetsMinutes: offsets });
      setEdit(e => ({ ...e, [idm]: undefined }));
      setNewIdm('');
      await load();
    } catch (e) {
      setError(errText(e, 'Failed to save.'));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Stop tracking ${t.name}? Their predicted car is forgotten too.`)) return;
    try {
      await api.delete(`/group-spending/tracked/${t.idm}`);
      await load();
    } catch (e) {
      setError(errText(e, 'Failed to remove.'));
    }
  };

  const trackedIds = new Set(list.map(t => t.idm));

  return (
    <div className="fb-card">
      <div className="fb-card-header">
        <span className="fb-card-title">Tracked managers · extra collection before qualifying closes · reset after race 17</span>
      </div>
      <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--c-text-muted)' }}>
        Everyone in Elite / Master 1–5 is collected once a day and 5 minutes after qualifying closes. Tracked managers are also
        collected at their own times before qualifying closes, and every change is posted to the tracked-managers Discord channel.
      </div>
      {error && <div style={{ padding: '0 16px 10px', color: 'var(--c-error)', fontSize: 13 }}>{error}</div>}

      {canEdit && (
        <div style={{ display: 'flex', gap: 8, padding: '0 16px 12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="fb-input" style={{ maxWidth: 150 }} value={addGroup} onChange={e => setAddGroup(e.target.value)} title="Group">
            {TRACKABLE_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select className="fb-input" style={{ maxWidth: 260 }} value={newIdm} onChange={e => setNewIdm(e.target.value)}>
            <option value="">{candidates.length ? `Manager from ${addGroup}…` : 'Loading…'}</option>
            {candidates.filter(c => !trackedIds.has(c.idm)).map(c => <option key={c.idm} value={c.idm}>{c.name}</option>)}
          </select>
          <input className="fb-input" style={{ maxWidth: 280 }} value={newOffsets} onChange={e => setNewOffsets(e.target.value)}
                 title="Times before qualifying closes, e.g. 25h, 24h30m, 2h" />
          <button className="fb-btn fb-btn-primary fb-btn-sm" disabled={!newIdm} onClick={() => save(newIdm, newOffsets)}>Track</button>
        </div>
      )}

      <div className="fb-table-wrap">
        <table className="fb-table fb-table-sm">
          <thead><tr><th>Manager</th><th>Group</th><th>Collect before qualifying closes</th><th></th></tr></thead>
          <tbody>
            {list.map(t => (
              <React.Fragment key={t.idm}>
                <tr>
                  <td style={{ fontWeight: 600 }}>{t.name}</td>
                  <td>{t.group}</td>
                  <td>
                    {canEdit && edit[t.idm] !== undefined ? (
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <input className="fb-input fb-input-sm" value={edit[t.idm]} onChange={e => setEdit(x => ({ ...x, [t.idm]: e.target.value }))} />
                        <button className="fb-btn fb-btn-primary fb-btn-sm" onClick={() => save(t.idm, edit[t.idm])}>Save</button>
                      </span>
                    ) : (formatOffsets(t.offsetsMinutes) || '—')}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button className="fb-btn fb-btn-secondary fb-btn-sm" onClick={() => setOpenCar(openCar === t.idm ? null : t.idm)}>🏎️ Car</button>{' '}
                    {canEdit && <>
                      <CollectButton label="↻ Collect" params={`idm=${t.idm}`} onDone={load} />{' '}
                      <button className="fb-btn fb-btn-ghost fb-btn-sm" onClick={() => setEdit(x => ({ ...x, [t.idm]: formatOffsets(t.offsetsMinutes) }))}>Edit</button>
                      <button className="fb-btn fb-btn-ghost fb-btn-sm" style={{ color: 'var(--c-error)' }} onClick={() => remove(t)}>Remove</button>
                    </>}
                  </td>
                </tr>
                {openCar === t.idm && (
                  <tr><td colSpan={4} style={{ background: '#fafafa', padding: 12 }}>
                    <CarView car={t.car} />
                    <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 6 }}>
                      Levels come from part purchases we could decode with confidence. “?” — not seen yet.
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--c-text-muted)', padding: 20 }}>No tracked managers.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
